import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Plus, Loader2, Trash2, Star, Eye, EyeOff, ExternalLink,
  BarChart2, Info, Building2, Zap, Type,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  slug: '',
  name: '',
  tagline: '',
  description: '',
  category: 'AI Agents',
  logo_url: '',
  affiliate_url: '',
  website_display: '',
  reward_summary: '',
  customer_benefit: '',
  tier_label: '',
  featured: true,
  active: true,
  display_order: 100,
};

export default function AdminAffiliatePartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // slug being edited, or null
  const [clicksPanel, setClicksPanel] = useState(null); // slug or null

  // One-click auto-add state — paste affiliate URL → backend fetches
  // metadata + monochromes the logo + inserts the partner in one shot.
  const [autoUrl, setAutoUrl] = useState('');
  const [autoCategory, setAutoCategory] = useState('AI Tools');
  const [autoBenefit, setAutoBenefit] = useState('');
  const [autoInvert, setAutoInvert] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);

  // Hero "Featured AI partners" display tuning. Backed by
  // platform_settings.partner_display_offset / partner_display_floor.
  // Read on mount + saved via PATCH /api/admin/hero-stats-config.
  const [heroCfg, setHeroCfg] = useState({
    partner_display_offset: 0,
    partner_display_floor: 0,
    live_active_partners: 0,
    computed_display: 0,
  });
  const [heroCfgSaving, setHeroCfgSaving] = useState(false);
  const [heroOffsetInput, setHeroOffsetInput] = useState('0');
  const [heroFloorInput, setHeroFloorInput] = useState('0');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/affiliate-partners`);
      setPartners(res.data.partners || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load hero display tuning settings once on mount.
  const loadHeroCfg = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/hero-stats-config`);
      setHeroCfg(res.data);
      setHeroOffsetInput(String(res.data.partner_display_offset ?? 0));
      setHeroFloorInput(String(res.data.partner_display_floor ?? 0));
    } catch (err) {
      // Silent — admin can still operate without this widget.
    }
  }, []);

  useEffect(() => { loadHeroCfg(); }, [loadHeroCfg]);

  const saveHeroCfg = async (e) => {
    e.preventDefault();
    setHeroCfgSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/api/admin/hero-stats-config`, {
        partner_display_offset: Number(heroOffsetInput) || 0,
        partner_display_floor: Number(heroFloorInput) || 0,
      });
      setHeroCfg(res.data);
      toast.success(`Hero shows ${res.data.computed_display} now`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setHeroCfgSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const { slug: _slug, ...patch } = form;
        await axios.patch(`${API_URL}/api/admin/affiliate-partners/${editing}`, patch);
        toast.success(`Updated ${form.name}`);
      } else {
        await axios.post(`${API_URL}/api/admin/affiliate-partners`, form);
        toast.success(`Added ${form.name}`);
      }
      setForm(EMPTY_FORM);
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    setForm({
      slug: p.slug,
      name: p.name || '',
      tagline: p.tagline || '',
      description: p.description || '',
      category: p.category || 'AI Agents',
      logo_url: p.logo_url || '',
      affiliate_url: p.affiliate_url || '',
      website_display: p.website_display || '',
      reward_summary: p.reward_summary || '',
      customer_benefit: p.customer_benefit || '',
      tier_label: p.tier_label || '',
      featured: !!p.featured,
      active: !!p.active,
      display_order: p.display_order ?? 100,
    });
    setEditing(p.slug);
    setShowForm(true);
  };

  const toggleActive = async (p) => {
    try {
      await axios.patch(`${API_URL}/api/admin/affiliate-partners/${p.slug}`, {
        active: !p.active,
      });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle failed');
    }
  };

  const remove = async (p) => {
    if (!confirm(`Delete ${p.name}? Clicks history will be erased.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/affiliate-partners/${p.slug}`);
      toast.success(`Deleted ${p.name}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // Replace auto-extracted icon-style logos with a clean text wordmark
  // rendered from the partner's `name`. Useful when the source site
  // only serves a tiny favicon/mark that looks like a postage stamp.
  const regenWordmark = async (p) => {
    if (!confirm(`Replace ${p.name}'s logo with a clean text wordmark of "${p.name}"?`)) return;
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/affiliate-partners/${p.slug}/regenerate-logo-wordmark`
      );
      toast.success(`Wordmark generated for ${p.name} (${res.data.file_kb} KB)`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wordmark generation failed');
    }
  };

  const autoAdd = async (e) => {
    e?.preventDefault?.();
    if (!autoUrl.trim()) return;
    setAutoLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/affiliate-partners/auto-add`, {
        affiliate_url: autoUrl.trim(),
        category: autoCategory.trim() || undefined,
        customer_benefit: autoBenefit.trim() || undefined,
        invert_logo: autoInvert,
      });
      const { partner, created, warnings } = res.data;
      if (created) {
        toast.success(`Added ${partner.name} as a featured partner`);
      } else {
        toast.message(`${partner.name} already existed — opened existing record`);
      }
      // Surface warnings (max 2) so the operator knows if a logo
      // fallback fired or a tagline placeholder was used — they may
      // want to PATCH the partner with refined copy.
      (warnings || []).slice(0, 2).forEach((w) => toast.warning(w));
      setAutoUrl('');
      setAutoBenefit('');
      setAutoInvert(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-add failed');
    } finally {
      setAutoLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-affiliate-partners-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black mb-1">Affiliate Partners</h1>
            <p className="text-neutral-400 text-sm">
              Curated third-party AI tools we promote via affiliate links (Dub.co, etc.).
              Listed on the public <code className="text-[#00F0FF]">/partners</code> page below paid Enterprise partners.
            </p>
          </div>
          <Button
            onClick={() => {
              setForm(EMPTY_FORM);
              setEditing(null);
              setShowForm(v => !v);
            }}
            className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold"
            data-testid="add-affiliate-partner-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {showForm ? 'Close' : 'Add Partner'}
          </Button>
        </div>

        <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-4 flex gap-3">
          <Info className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-100/80">
            <strong className="text-yellow-300">Marblism content review:</strong> per Marblism's
            partner agreement, any social post or YouTube content referencing their brand must
            be emailed to <code className="text-yellow-300">jesus@marblism.com</code> (cc{' '}
            <code className="text-yellow-300">jhorman@marblism.com</code>) within 24h of publishing.
            This listing itself on theailedger.ai counts as "official platform placement" and
            does not require review, but marketing campaigns do.
          </div>
        </div>

        {/* Hero "Featured AI partners" display tuning — backed by
            platform_settings.partner_display_offset / partner_display_floor.
            Lets the founder reconcile the gap between "active partners in DB"
            and "partners that actually render on /featured" without a code
            change. Today on prod: offset=4 (4 active partners missing logo
            so they don't render), floor=48 (launch-day baseline). When the
            gap closes, set offset=0 and the hero shows the raw live count. */}
        <form
          onSubmit={saveHeroCfg}
          className="rounded-xl border border-[#00F0FF]/20 bg-[#00F0FF]/[0.03] p-4 space-y-3"
          data-testid="hero-stats-tuning"
        >
          <div className="flex items-start gap-3">
            <BarChart2 className="w-5 h-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-white mb-1">
                Hero "Featured AI partners" stat tuning
              </h3>
              <p className="text-xs text-neutral-400">
                Homepage Slot 1 shows{' '}
                <code className="text-[#00F0FF]">max(floor, live partners − offset)</code>.
                Adjust to match what <code>/featured</code> actually renders.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div>
              <Label htmlFor="hero-offset" className="text-xs">Offset</Label>
              <Input
                id="hero-offset"
                type="number"
                min={0}
                value={heroOffsetInput}
                onChange={(e) => setHeroOffsetInput(e.target.value)}
                className="bg-neutral-900 border-white/10"
                data-testid="hero-offset-input"
              />
            </div>
            <div>
              <Label htmlFor="hero-floor" className="text-xs">Floor (minimum)</Label>
              <Input
                id="hero-floor"
                type="number"
                min={0}
                value={heroFloorInput}
                onChange={(e) => setHeroFloorInput(e.target.value)}
                className="bg-neutral-900 border-white/10"
                data-testid="hero-floor-input"
              />
            </div>
            <div className="text-xs text-neutral-300 space-y-0.5">
              <div>Live active: <span className="text-white font-semibold">{heroCfg.live_active_partners}</span></div>
              <div>Hero shows now: <span className="text-[#00F0FF] font-semibold" data-testid="hero-stats-computed">{heroCfg.computed_display}</span></div>
            </div>
            <Button
              type="submit"
              disabled={heroCfgSaving}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold"
              data-testid="save-hero-stats-btn"
            >
              {heroCfgSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </form>

        {/* One-click auto-add — paste a URL, get a brand-clean partner.
            Lives above the manual form because 95% of partner adds will
            now go through this path. The manual form stays as a power-user
            override for partners with weird sites the auto-fetcher can't
            handle. */}
        <form
          onSubmit={autoAdd}
          className="rounded-2xl border border-[#00FF94]/20 bg-gradient-to-br from-[#00FF94]/5 to-[#00F0FF]/5 p-5"
          data-testid="affiliate-auto-add-card"
        >
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-5 h-5 text-[#00FF94]" />
            <h2 className="text-lg font-bold text-white">Auto-add by URL</h2>
            <span className="text-[10px] uppercase tracking-wider text-[#00FF94] border border-[#00FF94]/30 bg-[#00FF94]/10 px-1.5 py-0.5 rounded">
              new
            </span>
          </div>
          <p className="text-xs text-neutral-400 mb-4 max-w-2xl">
            Paste the affiliate URL — we'll fetch the site, extract a name + tagline + description,
            convert the logo to pure-white brand-safe PNG, and add the partner. You can refine
            anything afterwards by clicking <em>Edit</em> on the partner row.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-7">
              <Label className="text-xs uppercase tracking-wider text-neutral-400 mb-1.5 block">
                Affiliate URL
              </Label>
              <Input
                type="url"
                value={autoUrl}
                onChange={(e) => setAutoUrl(e.target.value)}
                placeholder="https://example.com/?via=theailedger"
                className="bg-black/40 border-white/10 h-10 text-sm"
                data-testid="affiliate-auto-add-url"
                required
              />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs uppercase tracking-wider text-neutral-400 mb-1.5 block">
                Category
              </Label>
              <Input
                type="text"
                value={autoCategory}
                onChange={(e) => setAutoCategory(e.target.value)}
                placeholder="AI Tools"
                className="bg-black/40 border-white/10 h-10 text-sm"
                data-testid="affiliate-auto-add-category"
              />
            </div>
            <div className="md:col-span-2">
              <Button
                type="submit"
                disabled={autoLoading || !autoUrl.trim()}
                className="bg-[#00FF94] hover:bg-[#00FF94]/90 text-black font-semibold h-10 w-full"
                data-testid="affiliate-auto-add-submit"
              >
                {autoLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                Add
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mt-3">
            <div className="md:col-span-9">
              <Label className="text-xs uppercase tracking-wider text-neutral-400 mb-1.5 block">
                Customer benefit (optional, e.g. "14-day free trial")
              </Label>
              <Input
                type="text"
                value={autoBenefit}
                onChange={(e) => setAutoBenefit(e.target.value)}
                placeholder="14-day free trial — no credit card"
                className="bg-black/40 border-white/10 h-10 text-sm"
                data-testid="affiliate-auto-add-benefit"
              />
            </div>
            <div className="md:col-span-3 flex items-end">
              <label className="flex items-center gap-2 text-xs text-neutral-400 cursor-pointer pb-2">
                <input
                  type="checkbox"
                  checked={autoInvert}
                  onChange={(e) => setAutoInvert(e.target.checked)}
                  className="accent-[#00FF94]"
                  data-testid="affiliate-auto-add-invert"
                />
                Invert logo polarity
                <span className="text-neutral-600">(if first attempt looks empty)</span>
              </label>
            </div>
          </div>
        </form>

        {showForm && (
          <form
            onSubmit={submit}
            className="rounded-2xl border border-white/10 bg-neutral-900/50 p-6 space-y-4"
            data-testid="affiliate-partner-form"
          >
            <h2 className="text-xl font-bold">
              {editing ? `Editing: ${form.name}` : 'New affiliate partner'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Slug (URL-safe, no spaces)" required>
                <Input
                  value={form.slug}
                  disabled={!!editing}
                  onChange={(e) => setForm(f => ({ ...f, slug: e.target.value.toLowerCase() }))}
                  placeholder="marblism"
                  data-testid="field-slug"
                />
              </Field>
              <Field label="Company name" required>
                <Input
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Marblism"
                  data-testid="field-name"
                />
              </Field>
              <Field label="Tagline" required>
                <Input
                  value={form.tagline}
                  onChange={(e) => setForm(f => ({ ...f, tagline: e.target.value }))}
                  placeholder="Your AI Employees Dream Team"
                  data-testid="field-tagline"
                />
              </Field>
              <Field label="Category">
                <Input
                  value={form.category}
                  onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                  placeholder="AI Agents"
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="Description (20–2000 chars)" required>
                  <Textarea
                    rows={4}
                    value={form.description}
                    onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Marblism gives your business a team of AI employees that handle..."
                    data-testid="field-description"
                  />
                </Field>
              </div>
              <Field label="Logo URL (local /partners/... or full https://)" required>
                <Input
                  value={form.logo_url}
                  onChange={(e) => setForm(f => ({ ...f, logo_url: e.target.value }))}
                  placeholder="/partners/marblism-logo.png"
                  data-testid="field-logo"
                />
              </Field>
              <Field label="Affiliate URL (your tracked link)" required>
                <Input
                  value={form.affiliate_url}
                  onChange={(e) => setForm(f => ({ ...f, affiliate_url: e.target.value }))}
                  placeholder="https://marblism.link/jake-colson"
                  data-testid="field-affiliate-url"
                />
              </Field>
              <Field label="Website display text">
                <Input
                  value={form.website_display}
                  onChange={(e) => setForm(f => ({ ...f, website_display: e.target.value }))}
                  placeholder="marblism.com"
                />
              </Field>
              <Field label="Tier label (optional)">
                <Input
                  value={form.tier_label}
                  onChange={(e) => setForm(f => ({ ...f, tier_label: e.target.value }))}
                  placeholder="Ambassador / Growth / Strategic"
                />
              </Field>
              <Field label="Reward summary (admin-only note)">
                <Input
                  value={form.reward_summary}
                  onChange={(e) => setForm(f => ({ ...f, reward_summary: e.target.value }))}
                  placeholder="30% lifetime commission"
                />
              </Field>
              <Field label="Customer benefit (shown on card)">
                <Input
                  value={form.customer_benefit}
                  onChange={(e) => setForm(f => ({ ...f, customer_benefit: e.target.value }))}
                  placeholder="10% off for life"
                />
              </Field>
              <Field label="Display order (lower = first)">
                <Input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => setForm(f => ({ ...f, display_order: parseInt(e.target.value) || 100 }))}
                />
              </Field>
              <div className="flex items-center gap-6 md:col-span-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => setForm(f => ({ ...f, featured: e.target.checked }))}
                    data-testid="field-featured"
                  />
                  Featured (pinned at top)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))}
                  />
                  Active (visible publicly)
                </label>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold"
                data-testid="save-partner-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? 'Update' : 'Create'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowForm(false); setEditing(null); setForm(EMPTY_FORM); }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" /></div>
        ) : partners.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-white/10 rounded-xl">
            <Building2 className="w-12 h-12 text-neutral-700 mx-auto mb-3" />
            <p className="text-neutral-400">No affiliate partners yet. Click "Add Partner" to list one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm" data-testid="partners-table">
              <thead className="bg-neutral-900/60 text-neutral-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Partner</th>
                  <th className="text-left px-4 py-3">Category</th>
                  <th className="text-left px-4 py-3">Reward</th>
                  <th className="text-right px-4 py-3">Clicks</th>
                  <th className="text-center px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {partners.map((p) => (
                  <tr key={p.slug} data-testid={`partner-row-${p.slug}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {p.logo_url && (
                          <img
                            src={p.logo_url.startsWith('http') ? p.logo_url : p.logo_url}
                            alt={p.name}
                            className="w-10 h-10 object-contain rounded bg-black/40 p-1"
                          />
                        )}
                        <div>
                          <div className="font-semibold flex items-center gap-2">
                            {p.name}
                            {p.featured && <Star className="w-3.5 h-3.5 fill-[#00F0FF] text-[#00F0FF]" />}
                          </div>
                          <div className="text-xs text-neutral-500">{p.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-300">{p.category}</td>
                    <td className="px-4 py-3 text-neutral-300">{p.reward_summary || '—'}</td>
                    <td className="px-4 py-3 text-right font-mono">{p.click_count || 0}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={p.active
                        ? 'text-[#00FF94] text-xs font-semibold'
                        : 'text-neutral-500 text-xs font-semibold'}>
                        {p.active ? 'ACTIVE' : 'HIDDEN'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setClicksPanel(clicksPanel === p.slug ? null : p.slug)}
                          className="p-1.5 rounded hover:bg-white/10 text-neutral-400"
                          title="View click analytics"
                          data-testid={`clicks-btn-${p.slug}`}
                        >
                          <BarChart2 className="w-4 h-4" />
                        </button>
                        <a
                          href={p.affiliate_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded hover:bg-white/10 text-neutral-400"
                          title="Open affiliate URL"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => toggleActive(p)}
                          className="p-1.5 rounded hover:bg-white/10 text-neutral-400"
                          title={p.active ? 'Hide' : 'Show'}
                        >
                          {p.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => regenWordmark(p)}
                          className="p-1.5 rounded hover:bg-white/10 text-neutral-400"
                          title="Replace logo with a clean text wordmark of the brand name"
                          data-testid={`wordmark-btn-${p.slug}`}
                        >
                          <Type className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => startEdit(p)}
                          className="px-2 py-1 rounded text-xs bg-white/10 hover:bg-white/20"
                          data-testid={`edit-btn-${p.slug}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => remove(p)}
                          className="p-1.5 rounded hover:bg-red-500/10 text-red-400"
                          data-testid={`delete-btn-${p.slug}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {clicksPanel && <ClicksPanel slug={clicksPanel} onClose={() => setClicksPanel(null)} />}
      </div>
    </AdminLayout>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-neutral-400">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}

function ClicksPanel({ slug, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/affiliate-partners/${slug}/clicks`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Failed to load clicks');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);
  return (
    <div className="rounded-xl border border-white/10 bg-neutral-900/70 p-5" data-testid={`clicks-panel-${slug}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg">
          Click analytics — <span className="text-[#00F0FF]">{slug}</span>
          {data && <span className="ml-3 text-sm text-neutral-400">Total: {data.total_clicks}</span>}
        </h3>
        <button onClick={onClose} className="text-neutral-400 hover:text-white text-sm">Close</button>
      </div>
      {!data ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : data.recent_clicks.length === 0 ? (
        <p className="text-sm text-neutral-500">No clicks yet.</p>
      ) : (
        <div className="max-h-72 overflow-y-auto text-xs">
          <table className="w-full">
            <thead className="text-neutral-500">
              <tr>
                <th className="text-left py-1">When</th>
                <th className="text-left py-1">Referrer</th>
                <th className="text-left py-1">IP (anon)</th>
                <th className="text-left py-1">User Agent</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_clicks.map((c, i) => (
                <tr key={`${c.clicked_at || ''}-${i}`} className="border-t border-white/5">
                  <td className="py-1.5 text-neutral-300">
                    {new Date(c.clicked_at).toLocaleString()}
                  </td>
                  <td className="py-1.5 text-neutral-400 truncate max-w-[180px]">
                    {c.referer || '—'}
                  </td>
                  <td className="py-1.5 text-neutral-400 font-mono">{c.anon_ip || '—'}</td>
                  <td className="py-1.5 text-neutral-400 truncate max-w-[220px]">
                    {c.user_agent || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
